import React from 'react';
import { Inputs, PackageType, IndustryType, RevenueCadence, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
interface AssumptionsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
}
export function AssumptionsSidebar({
  isOpen,
  onClose,
  inputs,
  onChange
}: AssumptionsSidebarProps) {
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Handle numeric input changes - allow empty string, convert to number only when valid
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    // If empty, set to empty string to allow clearing
    if (value === '') {
      onChange({
        ...inputs,
        [field]: ''
      });
      return;
    }
    // Otherwise parse as float
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({
        ...inputs,
        [field]: numValue
      });
    }
  };
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    onChange({
      ...inputs,
      industry: newIndustry,
      ticket: defaults.ticket,
      gpm: defaults.gpm,
      salesFirst90Total: defaults.salesFirst90Total,
      salesVetMonthly: defaults.salesVetMonthly,
      revenueCadence: defaults.revenueCadence,
      residualTicket: defaults.residualTicket
    });
  };
  return <>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={onClose} data-id="element-1" />}

      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-full max-w-xs bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-2">
        <div className="h-full flex flex-col" data-id="element-3">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b-2 border-gray-200 bg-navy" data-id="element-4">
            <h2 className="text-lg font-bold text-white" data-id="element-5">
              Business Assumptions
            </h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-id="element-6">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7">
                <line x1="18" y1="6" x2="6" y2="18" data-id="element-8" />
                <line x1="6" y1="6" x2="18" y2="18" data-id="element-9" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4" data-id="element-10">
            <div className="space-y-4" data-id="element-11">
              {/* Company Info */}
              <div className="space-y-3" data-id="element-12">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-13">
                  Company Info
                </h3>

                <div data-id="element-14">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-15">
                    Company Name
                  </label>
                  <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-16" />
                </div>

                <div data-id="element-17">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-18">
                    Industry
                  </label>
                  <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-19">
                    <option value="" data-id="element-20">Select</option>
                    <option data-id="element-21">Windows</option>
                    <option data-id="element-22">Roofing</option>
                    <option data-id="element-23">Solar (Sales)</option>
                    <option data-id="element-24">Solar (Vertical)</option>
                    <option data-id="element-25">Solar (Community)</option>
                    <option data-id="element-26">Pest</option>
                    <option data-id="element-27">Fiber</option>
                    <option data-id="element-28">Deregulated Energy</option>
                    <option data-id="element-29">Hardscaping</option>
                    <option data-id="element-30">Landscaping</option>
                    <option data-id="element-31">Other</option>
                  </select>
                </div>

                <div data-id="element-32">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-33">
                    Partnership
                  </label>
                  <select value={inputs.pkg} onChange={e => handleChange('pkg', e.target.value as PackageType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none font-semibold" data-id="element-34">
                    <option value="" data-id="element-35">Select</option>

                    <option value="advantage" data-id="element-36">HIRE Advantage</option>
                    <option value="summit" data-id="element-37">HIRE Summit</option>
                  </select>
                </div>
              </div>

              {/* Campaign & Ad Spend */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-38">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-39">
                  Campaign & Ad Spend
                </h3>

                <div data-id="element-40">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-41">
                    Number of Campaigns
                  </label>
                  <input type="number" min="0.5" step="0.5" value={inputs.campaigns} onChange={e => handleNumericChange('campaigns', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-42" />
                </div>

                <div data-id="element-43">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-44">
                    Ad Spend per Month (per campaign)
                  </label>
                  <div className="relative" data-id="element-45">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-46">
                      $
                    </span>
                    <input type="number" min="0" step="100" value={inputs.adSpend} onChange={e => handleNumericChange('adSpend', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-47" />
                  </div>
                </div>

                <div data-id="element-48">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-49">
                    Ad Cost per Hire
                  </label>
                  <div className="relative" data-id="element-50">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-51">
                      $
                    </span>
                    <input type="number" min="1" step="10" value={inputs.cph} onChange={e => handleNumericChange('cph', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-52" />
                  </div>
                </div>
              </div>

              {/* Revenue & Margins */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-53">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-54">
                  Revenue & Margins
                </h3>

                <div data-id="element-55">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-56">
                    Revenue Cadence
                  </label>
                  <select value={inputs.revenueCadence} onChange={e => handleChange('revenueCadence', e.target.value as RevenueCadence)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-57">
                    <option value="one-time" data-id="element-58">One-Time Sale</option>
                    <option value="monthly" data-id="element-59">Monthly Residual</option>
                    <option value="quarterly" data-id="element-60">Quarterly Residual</option>
                  </select>
                </div>

                <div data-id="element-61">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-62">
                    {inputs.revenueCadence === 'one-time' ? 'Average Ticket' : 'Initial Sale Ticket'}
                  </label>
                  <div className="relative" data-id="element-63">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-64">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-65" />
                  </div>
                </div>

                {inputs.revenueCadence !== 'one-time' && <div data-id="element-66">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-67">
                      {inputs.revenueCadence === 'monthly' ? 'Monthly Residual' : 'Quarterly Residual'}
                    </label>
                    <div className="relative" data-id="element-68">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-69">
                        $
                      </span>
                      <input type="number" min="0" step="10" value={inputs.residualTicket || 0} onChange={e => handleNumericChange('residualTicket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-70" />
                    </div>
                  </div>}

                {inputs.revenueCadence === 'monthly' && <div data-id="element-71">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-72">
                      Monthly Attrition
                    </label>
                    <div className="relative" data-id="element-73">
                      <input type="number" min="0" max="100" step="1" value={inputs.monthlyAttrition || 2} onChange={e => handleNumericChange('monthlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-74" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-75">
                        %
                      </span>
                    </div>
                  </div>}

                {inputs.revenueCadence === 'quarterly' && <div data-id="element-76">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-77">
                      Quarterly Attrition
                    </label>
                    <div className="relative" data-id="element-78">
                      <input type="number" min="0" max="100" step="1" value={inputs.quarterlyAttrition || 5} onChange={e => handleNumericChange('quarterlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-79" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-80">
                        %
                      </span>
                    </div>
                  </div>}

                <div data-id="element-81">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-82">
                    Gross Profit Margin
                  </label>
                  <div className="relative" data-id="element-83">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-84" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-85">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Sales Performance */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-86">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-87">
                  Sales Performance
                </h3>

                <div data-id="element-88">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-89">
                    Sales per 90 Day Rep
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesFirst90Total} onChange={e => handleNumericChange('salesFirst90Total', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-90" />
                </div>

                <div data-id="element-91">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-92">
                    Monthly Sales per Veteran
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => handleNumericChange('salesVetMonthly', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-93" />
                </div>

                <div data-id="element-94">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-95">
                    Pullthrough / Realization
                  </label>
                  <div className="relative" data-id="element-96">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-97" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-98">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Retention Model */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-99">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-100">
                  Retention Model
                </h3>

                <div data-id="element-101">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-102">
                    Hired to Launched
                  </label>
                  <div className="relative" data-id="element-103">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-104" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-105">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-106">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-107">
                    Launched to Day 30
                  </label>
                  <div className="relative" data-id="element-108">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-109" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-110">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-111">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-112">
                    Day 30 to Day 90
                  </label>
                  <div className="relative" data-id="element-113">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-114" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-115">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-116">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-117">
                    Day 90 to 1 Year
                  </label>
                  <div className="relative" data-id="element-118">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-119" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-120">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}